import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { Tag } from 'primereact/tag'
import { classNames } from 'primereact/utils'
import InfoTip from '../../../components/novarium/InfoTip'
import { UrgencyWhereRowDTO } from '../../../dto/urgencySummary/UrgencySummaryDTO'
import { LEVEL_LABEL_ID, LEVEL_TAG_CLASS, RANKING_LEVEL } from '../../ServicesStatistics/waitLevelText'
import { isCriticalUrgencyCause, MAX_CAUSES_SHOWN } from '../urgencyCauseText'
import { formatCount, formatMinutes } from '../urgencySummaryFormat'
import { SectionStatus } from '../useSectionData'
import UrgencyWaitCauses from './UrgencyWaitCauses'

interface UrgencyWhereProps {
  /** Una fila por guardia con llegadas, de mayor a menor espera media. */
  rows: UrgencyWhereRowDTO[]
  status: SectionStatus
  /** Los datos que se ven no son los de los últimos filtros: se están recalculando. */
  refreshing: boolean
  /** Guardia aplicada como filtro; null si son todas. Con una elegida hay una sola fila que explica su espera. */
  selectedTemplateId: number | null
  /** Tocar la fila: aplica la guardia como filtro de la pantalla. */
  onSelectTemplate: (templateId: number) => void
  /** «Reintentar» cuando no se pudo calcular. */
  onRetry: () => void
}

/**
 * «Dónde y cuándo se espera»: una fila por guardia, de mayor a menor espera media, con su nivel (con texto, no depende del
 * color), las llegadas y sus posibles causas (hasta tres, una por renglón). Cómo sale el nivel (con el objetivo provisorio por
 * código) y qué son las causas se lee en el ⓘ del título. Con varias guardias, toda la fila es un botón
 * que filtra la pantalla por esa guardia, también con el teclado; con una guardia elegida hay una sola fila («Qué explica
 * la espera») que no filtra nada. El backend calcula el nivel y las causas: acá solo se muestran. Si no se pudo calcular,
 * un texto con «Reintentar»: el resto de la pantalla sigue funcionando.
 * TODO(design-system): el ranking no está en Novarium; usa filas como la lista de avisos de Estadísticas de atención y la
 * cabecera de una card. El resalte de la fila con espera larga en Inmediato o Emergencia (barra a la izquierda, ícono y
 * negrita) tampoco está definido.
 */
export default function UrgencyWhere({ rows, status, refreshing, selectedTemplateId, onSelectTemplate, onRetry }: UrgencyWhereProps) {
  const intl = useIntl()
  const selected = selectedTemplateId !== null
  const shown = selected ? rows.filter((row) => row.templateId === selectedTemplateId) : rows
  // Con una sola guardia, tocar la fila filtraría por todo lo que ya se ve: no se ofrece
  const clickable = !selected && shown.length > 1

  return (
    <section className="nv-card nv-urg-where" aria-labelledby="nv-urg-where-title" aria-busy={status === 'loading' || refreshing}>
      <div className="nv-urg-where__head">
        <h2 className="nv-section-title" id="nv-urg-where-title">
          {intl.formatMessage({ id: selected ? 'UrgencySummaryWhereTitleSelected' : 'UrgencySummaryWhereTitle' })}
        </h2>
        <InfoTip text={intl.formatMessage({ id: 'UrgencySummaryWhereHint' })} />
      </div>
      {status === 'loading' && shown.length === 0 && (
        <p className="nv-note" role="status">
          <i className="pi pi-info-circle" aria-hidden="true" />
          {intl.formatMessage({ id: 'UrgencySummaryWhereLoading' })}
        </p>
      )}
      {status === 'failed' && (
        <div className="nv-urg-failed" role="status">
          <p className="nv-note">
            <i className="pi pi-info-circle" aria-hidden="true" />
            {intl.formatMessage({ id: 'UrgencySummaryWhereFailed' })}
          </p>
          <Button type="button" link label={intl.formatMessage({ id: 'UrgencySummaryRetry' })} onClick={onRetry} />
        </div>
      )}
      {status === 'ready' && shown.length === 0 && (
        <p className="nv-note">
          <i className="pi pi-info-circle" aria-hidden="true" />
          {intl.formatMessage({ id: 'UrgencySummaryWhereEmpty' })}
        </p>
      )}
      {status !== 'failed' && shown.length > 0 && (
        <ul className={classNames('nv-urg-where__list', { 'nv-urg-blurred': refreshing || status === 'loading' })}>
          {shown.map((row) => {
            const level = RANKING_LEVEL[row.level]
            const priority = row.causes.slice(0, MAX_CAUSES_SHOWN).some(isCriticalUrgencyCause)
            const rowClass = classNames('nv-urg-where__row', {
              'nv-urg-where__row--selected': selected,
              'nv-urg-where__row--priority': priority,
              'nv-urg-where__row--action': clickable,
            })
            const content = (
              <>
                {!selected && <span className="nv-urg-where__name" title={row.templateName}>{row.templateName}</span>}
                <span className="nv-urg-where__wait">
                  <span className="nv-urg-where__minutes">{formatMinutes(intl, row.avgWaitMinutes)}</span>
                  {/* Sin atendidos con espera medible no hay nivel que mostrar */}
                  {row.avgWaitMinutes !== null && (
                    <Tag className={LEVEL_TAG_CLASS[level]} value={intl.formatMessage({ id: LEVEL_LABEL_ID[level] })} />
                  )}
                </span>
                <span className="nv-urg-where__arrivals">
                  {intl.formatMessage(
                    { id: 'UrgencySummaryWhereArrivals' },
                    { count: row.arrivals, countText: formatCount(intl, row.arrivals) },
                  )}
                </span>
                <UrgencyWaitCauses causes={row.causes} level={row.level} />
              </>
            )
            return (
              <li key={row.templateId} className="nv-urg-where__item">
                {clickable ? (
                  <div
                    className={rowClass}
                    role="button"
                    tabIndex={0}
                    aria-label={intl.formatMessage({ id: 'UrgencySummaryWhereRowLabel' }, { name: row.templateName })}
                    onClick={() => onSelectTemplate(row.templateId)}
                    onKeyDown={(event) => {
                      // Lo que tenga adentro (una etiqueta con tooltip) maneja su propio teclado
                      if (event.target !== event.currentTarget) return
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onSelectTemplate(row.templateId)
                      }
                    }}
                  >
                    {content}
                    <i className="pi pi-chevron-right nv-urg-where__chevron" aria-hidden="true" />
                  </div>
                ) : (
                  <div className={rowClass}>{content}</div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
